import type { AxiosResponse } from "axios";
import { storeToRefs } from "pinia";

import { $streemApiV1 } from "shared/boot/api";
import FilterSetFilters from "shared/helpers/FilterSetFilters";
import {
  defaultContentToggles,
  getSelectedContentForMedia,
  getSources,
  type Medium,
} from "shared/helpers/media";
import {
  type SourceParams,
  sourcesToParams,
} from "shared/helpers/sourceFilters";
import type StreamFilters from "shared/helpers/StreamFilters";
import { useFilterSetsStore } from "shared/stores/filterSets";
import { type OrganisationFeaturesContentToggle } from "shared/stores/organisationFeatures";
import { useStreamsStore } from "shared/stores/streams";
import type { CurrentUser } from "shared/stores/user/types";
import type { FilterSet, Maybe, Stream } from "shared/types";
import type { SocialMentionType } from "shared/types/mentions";
import { isSocialMentionType } from "shared/types/mentions/guards";

export async function applyFilterSetToStreams(
  filterSet: FilterSet,
  streamIds: number[]
) {
  const response = await $streemApiV1.post(
    `filter_sets/${filterSet.id}/apply_to_streams`,
    {
      params: {
        stream_ids: streamIds,
      },
    }
  );

  const updatedFilterSet = response.data;
  const filterSetsStore = useFilterSetsStore();
  const { updateFilterSet: updateFilterSetInStore } = filterSetsStore;

  updateFilterSetInStore(updatedFilterSet);

  return response;
}

export async function createFilterSet(
  filterSet: FilterSet
): Promise<AxiosResponse<FilterSet>> {
  const params = mapToParams(filterSet);

  const response = await $streemApiV1.post("filter_sets", { params });

  const createdFilterSet = response.data;
  const filterSetsStore = useFilterSetsStore();
  const { addFilterSet: addFilterSetToStore } = filterSetsStore;

  addFilterSetToStore(createdFilterSet);

  return response;
}

export function getFilterSet(
  filterSetId: number
): Promise<AxiosResponse<FilterSet>> {
  return $streemApiV1.get(`filter_sets/${filterSetId}`);
}

export function createBlankFilterSet(currentUser: CurrentUser): FilterSet {
  const {
    selected_magazine_content: selectedMagazineContent,
    selected_online_content: selectedOnlineContent,
    selected_podcast_content: selectedPodcastContent,
    selected_print_content: selectedPrintContent,
    selected_radio_content: selectedRadioContent,
    selected_social_content: selectedSocialContent,
    selected_tv_content: selectedTvContent,
  } = defaultContentToggles();

  const convertedSelectedSocialContent: Maybe<SocialMentionType[]> =
    selectedSocialContent?.filter((type) => isSocialMentionType(type));

  const defaultFilterSetContentToggles = {
    selected_magazine_content: selectedMagazineContent,
    selected_online_content: selectedOnlineContent,
    selected_podcast_content: selectedPodcastContent,
    selected_print_content: selectedPrintContent,
    selected_radio_content: selectedRadioContent,
    selected_social_content: convertedSelectedSocialContent,
    selected_tv_content: selectedTvContent,
  };

  const defaultMedia = getSources().map((source: Medium) => source.medium);

  return {
    enabled_media: defaultMedia,
    filters: [],
    label: "",
    magazine_content: defaultMedia.includes("Magazine"),
    online_content: defaultMedia.includes("Online"),
    options: {},
    organisation_id: currentUser.organisation.id,
    podcast_content: defaultMedia.includes("Podcast"),
    print_content: defaultMedia.includes("Print"),
    radio_content: defaultMedia.includes("Radio"),
    social_content: defaultMedia.includes("Influencer Social"),
    sources: [],
    tv_content: defaultMedia.includes("TV"),
    user_id: currentUser.id,
    ...defaultFilterSetContentToggles,
  };
}

export function createDisplayStreamFromFilterSet(
  stream: Stream,
  filterSet: FilterSet
): Stream {
  const contentForMedia = getSelectedContentForMedia(
    filterSet.options?.media || []
  );

  const content = Object.keys(contentForMedia).reduce((obj, key) => {
    const keyTyped = key as keyof OrganisationFeaturesContentToggle;

    return {
      ...obj,
      [keyTyped]: contentForMedia[keyTyped],
      [keyTyped.replace("selected_", "")]: Boolean(
        contentForMedia[keyTyped]?.length
      ),
    };
  }, {});

  const displayStream = {
    ...stream,
    enabled_media: filterSet.enabled_media,
    ...content,
  };

  return displayStream;
}

export function updateFilterSet(
  filterSet: FilterSet
): Promise<AxiosResponse<FilterSet>> {
  const params = mapToParams(filterSet);

  return $streemApiV1.put(`filter_sets/${filterSet.id}`, {
    params,
  });
}

export async function getFilterSets(): Promise<FilterSet[]> {
  const filterSetsStore = useFilterSetsStore();
  const { filterSets, filterSetsLoaded } = storeToRefs(filterSetsStore);

  const { getFilterSets: getFilterSetsForStore } = filterSetsStore;

  if (!filterSetsLoaded.value) {
    await getFilterSetsForStore();
  }

  return filterSets.value;
}

export function isReadOnly(
  filterSet: FilterSet,
  currentUserId: number
): boolean {
  return (
    filterSet.user_accessibles?.some(
      (share) =>
        share.user_id === currentUserId && share.access_level === "read_only"
    ) || false
  );
}

export function filtersFromStreamFilters(
  streamFilters: StreamFilters,
  currentUser: CurrentUser
): FilterSetFilters {
  const {
    bookmark_stream_ids: bookmarkStreamIds,
    excluded_bookmark_stream_ids: excludedBookmarkStreamIds,
    ...streamOptions
  } = streamFilters.toOptions();

  const streamsStore = useStreamsStore();
  const { bookmarkStreams } = storeToRefs(streamsStore);

  const tags = bookmarkStreams.value.flatMap(({ color, id, label }) =>
    bookmarkStreamIds?.includes(id) ? [{ color, id, label }] : []
  );

  const excludedTags = bookmarkStreams.value.flatMap(({ color, id, label }) =>
    excludedBookmarkStreamIds?.includes(id) ? [{ color, id, label }] : []
  );

  return FilterSetFilters.fromOptions(
    streamOptions,
    createBlankFilterSet(currentUser),
    {
      excludedSources: streamFilters.excludedSources || [],
      excludedTags,
      sources: streamFilters.sources || [],
      tags,
    }
  );
}

export function filtersFromFilterSet(filterSet: FilterSet): FilterSetFilters {
  const {
    bookmark_stream_ids: bookmarkStreamIds,
    excluded_bookmark_stream_ids: excludedBookmarkStreamIds,
    ...filterSetOptions
  } = filterSet.options || {};

  const streamsStore = useStreamsStore();
  const { bookmarkStreams } = storeToRefs(streamsStore);

  const tags = bookmarkStreams.value.flatMap(({ color, id, label }) =>
    bookmarkStreamIds?.includes(id) ? [{ color, id, label }] : []
  );

  const excludedTags = bookmarkStreams.value.flatMap(({ color, id, label }) =>
    excludedBookmarkStreamIds?.includes(id) ? [{ color, id, label }] : []
  );

  return FilterSetFilters.fromOptions(filterSetOptions, filterSet, {
    excludedSources:
      filterSet.sources?.filter((source) => source.action === "exclude") || [],
    excludedTags,
    sources:
      filterSet.sources?.filter((source) => source.action === "include") || [],
    tags,
  });
}

export function streamFiltersFromFilterSet(
  filterSet: FilterSet,
  filters: StreamFilters
): StreamFilters {
  const localFilters = filters.clone();
  const filterSetFilters = filtersFromFilterSet(filterSet);

  const {
    booleanQuery,
    excludedSources,
    keywords,
    media,
    simpleBoolean,
    sources,
  } = filterSetFilters;

  return Object.assign(localFilters, {
    booleanQuery,
    excludedSources,
    keywords,
    media,
    simpleBoolean,
    sources,
  });
}

function mapToParams(
  filterSet: FilterSet
): Omit<FilterSet, "sources"> & { sources: SourceParams[] } {
  const sourceParams = [];

  if (filterSet.sources?.length) {
    sourceParams.push(
      ...sourcesToParams(
        filterSet.sources.filter((source) => source.action === "include"),
        "include"
      )
    );

    sourceParams.push(
      ...sourcesToParams(
        filterSet.sources.filter((source) => source.action === "exclude"),
        "exclude"
      )
    );
  }

  return {
    ...filterSet,
    sources: sourceParams,
  };
}
